import { apiFetch } from "./api";
import { appState } from "./state";
import { normalizeEmail } from "./utils";
import type { DailyRewardResult } from "./types";

// Single authoritative place that calls GET /api/me to answer "is the
// server session actually still valid" - reuses the existing identity
// endpoint (AuthGate.tsx's old checkSession() already called this exact
// route) rather than adding a new one. Every long-idle recovery trigger
// (resume from background, bfcache restore, network back online, repeated
// WS reconnect failure - all wired in AuthGate.tsx) funnels through
// validateSession() here, so there is exactly one place issuing this
// request, with dedup + a staleness threshold, instead of N independent
// checks scattered across components.

export interface SessionValidationResult {
  authenticated: boolean;
  mustResetPassword: boolean;
  dailyReward: DailyRewardResult | null;
}

// How recently validateSession() must have succeeded for a caller to treat
// the session as "still known good" without making another round trip.
// 5 minutes mirrors the server's own cleanExpiredSessions() sweep cadence
// (server.js) - there's no point checking more often than the server
// itself reconciles expired sessions, and this keeps resume-triggered
// checks (which can fire from several listeners near-simultaneously - see
// AuthGate.tsx) from ever hammering the endpoint.
const STALE_THRESHOLD_MS = 5 * 60 * 1000;

let lastValidatedAt = 0;
let inFlight: Promise<SessionValidationResult> | null = null;

export function isSessionStale(): boolean {
  return !lastValidatedAt || Date.now() - lastValidatedAt > STALE_THRESHOLD_MS;
}

// A bfcache restore (pageshow with event.persisted) freezes the entire JS
// heap, including lastValidatedAt itself - the elapsed wall-clock time
// while frozen is exactly the kind of long-idle gap this exists to catch,
// so that specific resume path forces a real check regardless of the
// timestamp rather than trusting a "recent" validation that predates the
// freeze.
export function forceStale(): void {
  lastValidatedAt = 0;
}

// Dedupes concurrent callers into one in-flight request - several resume
// triggers (visibilitychange + pageshow + online) can land within the same
// tick, and this must not fire N simultaneous /api/me calls for that.
export async function validateSession(): Promise<SessionValidationResult> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    console.log("[SESSION] validation started");
    try {
      const res = await apiFetch("/api/me");
      if (!res.ok) {
        console.log("[SESSION] validation failed - not authenticated");
        return { authenticated: false, mustResetPassword: false, dailyReward: null };
      }
      const data = await res.json();
      const authenticated = !!data.authenticated;
      if (authenticated) {
        lastValidatedAt = Date.now();
        appState.currentUserEmail = data.email ? normalizeEmail(data.email) : "";
        appState.currentUserFullName = data.fullName || "";
        appState.currentUserAvatarFilename = data.avatarFilename || null;
      }
      console.log(`[SESSION] validation ${authenticated ? "succeeded" : "failed - not authenticated"}`);
      return {
        authenticated,
        mustResetPassword: !!data.mustResetPassword,
        dailyReward: data.dailyReward || null,
      };
    } catch {
      console.log("[SESSION] validation failed - network error");
      return { authenticated: false, mustResetPassword: false, dailyReward: null };
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
